import React from 'react';
import { Gift, Heart, AlertTriangle, CheckCircle, Filter, Search, X } from 'lucide-react';
import { formatCurrency } from '../utils/formatting';
import { PrimeIndemnite, PrimesData } from '../types';
import { 
  calculateAdjustedPrimeAmount
} from '../utils/primeCalculations';
import LoadingSpinner, { SkeletonPrime } from './ui/LoadingSpinner';
import { usePrimesManagement } from '../hooks/usePrimesManagement';
import PrimesMessages from './PrimesMessages';
import PrimesList from './PrimesList';
import PrimesInfoSection from './PrimesInfoSection';
import PrimesCategoryAccordion from './PrimesCategoryAccordion';
import { GradeData, Profession } from '../types';

interface PrimesIndemnitesProps {
  className?: string;
  primesData: PrimesData;
  onPrimesChange: (primesData: PrimesData) => void;
  onMarkPrimesModified: () => void;
  workTimePercentage?: number;
  selectedGrade?: number | null;
  selectedEchelon?: number | null;
  currentProfessionId?: string;
  selectedStatus?: StatusType;
  isLoading?: boolean;
  grades: GradeData[];
  selectedProfession: Profession;
}

export default function PrimesIndemnites({ 
  className = "", 
  primesData, 
  onPrimesChange, 
  onMarkPrimesModified,
  workTimePercentage = 100,
  selectedGrade = null,
  selectedEchelon = null,
  currentProfessionId = 'infirmier',
  selectedStatus = 'titulaire',
  isLoading = false,
  grades,
  selectedProfession
}: PrimesIndemnitesProps) {
  console.log('🔍 PrimesIndemnites - Component rendered with:');
  console.log('🔍 PrimesIndemnites - currentProfessionId:', currentProfessionId);
  console.log('🔍 PrimesIndemnites - selectedProfession:', selectedProfession);
  console.log('🔍 PrimesIndemnites - primesData.primes.length:', primesData.primes.length);
  console.log('🔍 PrimesIndemnites - selectedGrade:', selectedGrade);
  console.log('🔍 PrimesIndemnites - selectedEchelon:', selectedEchelon);
  
  // États pour le filtrage
  const [selectedPrimeTypeFilter, setSelectedPrimeTypeFilter] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  // Utilisation du hook de gestion des primes
  const {
    // États
    editingPrime,
    editingBaseAmount,
    editingReimbursementRate,
    expandedPrimes,
    showSuccessMessage,
    selectedZoneIR,
    expandedCalculationDetails,
    
    // Données calculées
    currentIndiceMajore,
    primesWithCalculatedAmounts,
    isCalculatingPrimes,
    validationErrors,
    gradeEchelonValidation,
    
    // Gestionnaires d'événements
    handleTogglePrime,
    handleToggleDetails,
    handleToggleCalculationDetails,
    handleEditAmount,
    handleSaveAmount,
    handleCancelEditAmount,
    handleEditBaseAmount,
    handleSaveBaseAmount,
    handleCancelEditBaseAmount,
    handleEditReimbursementRate,
    handleSaveReimbursementRate,
    handleCancelEditReimbursementRate,
    handleIFTDConfigChange,
    handleContagionConfigChange,
    handlePrimeNuitConfigChange,
    handleSFTConfigChange,
    handleInsalubriteCat2ConfigChange,
    handleISSConfigChange,
    handleNBIConfigChange,
    handleTransportBaseAmountChange,
    handleZoneChange,
    getDocumentationUrl
  } = usePrimesManagement({
    selectedGrade,
    selectedEchelon,
    workTimePercentage,
    primesData,
    currentProfessionId,
    selectedStatus,
    isLoading,
    grades,
    selectedProfession,
    onPrimesChange,
    onMarkPrimesModified
  });

  // Extraire les types de primes uniques pour les filtres
  const uniquePrimeTypes = React.useMemo(() => {
    const types = new Set<string>();
    primesWithCalculatedAmounts.forEach(prime => {
      if (prime.primeType) {
        types.add(prime.primeType);
      }
    });
    return Array.from(types).sort();
  }, [primesWithCalculatedAmounts]);

  // Filtrer les primes selon le type sélectionné et la recherche
  const filteredPrimes = React.useMemo(() => {
    let filtered = primesWithCalculatedAmounts;
    
    // Filtrage par type de prime
    if (selectedPrimeTypeFilter) {
      if (selectedPrimeTypeFilter === 'actives') {
        filtered = filtered.filter(prime => prime.active);
      } else {
        filtered = filtered.filter(prime => prime.primeType === selectedPrimeTypeFilter);
      }
    }
    
    // Filtrage par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(prime => 
        prime.nom.toLowerCase().includes(query) ||
        prime.description.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [primesWithCalculatedAmounts, selectedPrimeTypeFilter, searchQuery]);

  // IDs des primes "recommandées" selon le profil
  const recommendedPrimeIds = React.useMemo(() => {
    const ids = new Set<string>();
    // CTI / Ségur : tous les titulaires/stagiaires
    if (selectedStatus === 'titulaire' || selectedStatus === 'stagiaire') {
      ids.add('cti');
      ids.add('segur');
      ids.add('nbi');
      ids.add('prime_service');
    }
    // Indemnité de résidence partout
    ids.add('indemnite_residence');
    // SFT courant
    ids.add('sft');
    // Prime de nuit souvent activée en milieu hospitalier
    ids.add('prime_nuit');
    return ids;
  }, [selectedStatus]);

  const recommendedPrimes = React.useMemo(
    () => filteredPrimes.filter(p => recommendedPrimeIds.has(p.id)),
    [filteredPrimes, recommendedPrimeIds]
  );

  const activePrimesList = React.useMemo(
    () => filteredPrimes.filter(p => p.active),
    [filteredPrimes]
  );

  const nonRecommendedOtherPrimes = React.useMemo(
    () => filteredPrimes.filter(p => !recommendedPrimeIds.has(p.id) && !p.active),
    [filteredPrimes, recommendedPrimeIds]
  );

  // Regrouper les primes non recommandées et non actives par catégorie
  const primesByCategory = React.useMemo(() => {
    const map = new Map<string, PrimeIndemnite[]>();
    nonRecommendedOtherPrimes.forEach(p => {
      const key = p.primeType || 'Autres';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [nonRecommendedOtherPrimes]);

  const hasSearchOrFilter = !!searchQuery.trim() || !!selectedPrimeTypeFilter;

  // Compter les primes actives
  const activePrimesCount = primesWithCalculatedAmounts.filter(p => p.active).length;

  // Show loading state
  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <Gift className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Primes et indemnités</h2>
          <LoadingSpinner size="sm" className="ml-auto" text="Chargement des primes..." />
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonPrime key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Don't show the component if grade and echelon validation fails
  if (!gradeEchelonValidation.isValid) {
    return (
      <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Gift className="w-6 h-6 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-400">Primes et indemnités</h2>
        </div>
        
        <div className="text-center py-8">
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h3 className="font-medium text-yellow-800">Configuration requise</h3>
            </div>
            <div className="text-sm text-yellow-700">
              {gradeEchelonValidation.errors.map((error, index) => (
                <p key={index}>• {error}</p>
              ))}
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            <p className="mb-2">Une fois votre grade et échelon renseignés, vous pourrez :</p>
            <ul className="text-left max-w-md mx-auto space-y-1">
              <li>• Activer/désactiver vos primes</li>
              <li>• Configurer les montants personnalisés</li>
              <li>• Voir le calcul automatique des primes calculables</li>
              <li>• Consulter la documentation officielle</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-3 md:p-5 ${className}`}>
      {isCalculatingPrimes && (
        <div className="mb-3 flex justify-end">
          <LoadingSpinner size="sm" text="Calcul..." />
        </div>
      )}
      <>
          {/* Messages de validation et de succès */}
          <PrimesMessages
            gradeEchelonValidation={gradeEchelonValidation}
            showSuccessMessage={showSuccessMessage}
            validationErrors={validationErrors}
          />

          {/* Filtres par catégorie de prime */}
          <div className="mb-3 space-y-2">
            {/* Barre de recherche compacte */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une prime..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Effacer la recherche"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {searchQuery.trim() && (
              <div className="text-[11px] text-gray-600">
                {filteredPrimes.length} résultat{filteredPrimes.length > 1 ? 's' : ''} pour "{searchQuery}"
              </div>
            )}

            {/* Filtres par catégorie de prime */}
            {uniquePrimeTypes.length > 0 && (
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                <Filter className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <button
                  onClick={() => setSelectedPrimeTypeFilter(null)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                    selectedPrimeTypeFilter === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Toutes ({primesWithCalculatedAmounts.length})
                </button>
                <button
                  onClick={() => setSelectedPrimeTypeFilter('actives')}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                    selectedPrimeTypeFilter === 'actives'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Actives ({activePrimesCount})
                </button>
                {uniquePrimeTypes.map((primeType) => {
                  const count = primesWithCalculatedAmounts.filter((p) => p.primeType === primeType).length;
                  return (
                    <button
                      key={primeType}
                      onClick={() => setSelectedPrimeTypeFilter(primeType)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                        selectedPrimeTypeFilter === primeType
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {primeType} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            {selectedPrimeTypeFilter && (
              <div className="text-[11px] text-gray-600">
                {filteredPrimes.length} prime{filteredPrimes.length > 1 ? 's' : ''}
                {selectedPrimeTypeFilter === 'actives'
                  ? ` active${filteredPrimes.length > 1 ? 's' : ''}`
                  : ` de type "${selectedPrimeTypeFilter}"`}
                {searchQuery.trim() && ` correspondant à "${searchQuery}"`}
              </div>
            )}
          </div>

          {/* Liste des primes */}
          {(() => {
            const commonListProps = {
              workTimePercentage,
              selectedStatus,
              selectedGrade,
              selectedEchelon: selectedEchelon ?? undefined,
              editingPrime,
              editingBaseAmount,
              editingReimbursementRate,
              expandedPrimes,
              selectedZoneIR,
              currentIndiceMajore,
              onTogglePrime: handleTogglePrime,
              onToggleDetails: handleToggleDetails,
              onEditAmount: handleEditAmount,
              onSaveAmount: handleSaveAmount,
              onCancelEditAmount: handleCancelEditAmount,
              onEditBaseAmount: handleEditBaseAmount,
              onSaveBaseAmount: handleSaveBaseAmount,
              onCancelEditBaseAmount: handleCancelEditBaseAmount,
              onEditReimbursementRate: handleEditReimbursementRate,
              onSaveReimbursementRate: handleSaveReimbursementRate,
              onCancelEditReimbursementRate: handleCancelEditReimbursementRate,
              onZoneChange: handleZoneChange,
              onIFTDConfigChange: handleIFTDConfigChange,
              onContagionConfigChange: handleContagionConfigChange,
              onPrimeNuitConfigChange: handlePrimeNuitConfigChange,
              onSFTConfigChange: handleSFTConfigChange,
              onTransportBaseAmountChange: handleTransportBaseAmountChange,
              onInsalubriteCat2ConfigChange: handleInsalubriteCat2ConfigChange,
              onISSConfigChange: handleISSConfigChange,
              onNBIConfigChange: handleNBIConfigChange,
              onAmountChange: (amount: number) => {
                const updatedPrimes = primesWithCalculatedAmounts.map(p => {
                  if (p.id === editingPrime) {
                    if (p.id === 'transport' && p.montantBase && p.pourcentageRemboursement) {
                      const newBaseAmount = amount / (p.pourcentageRemboursement / 100);
                      return { ...p, montantUtilisateur: amount, montantBase: newBaseAmount };
                    }
                    return { ...p, montantUtilisateur: amount };
                  }
                  return p;
                });
                const newPrimesData = {
                  primes: updatedPrimes,
                  totalMensuel: updatedPrimes
                    .filter(p => p.active)
                    .reduce((total, prime) => total + calculateAdjustedPrimeAmount(prime, workTimePercentage), 0)
                };
                onPrimesChange(newPrimesData);
                onMarkPrimesModified();
              },
              getDocumentationUrl,
              onPrimesChange,
              onMarkPrimesModified,
              expandedCalculationDetails,
              onToggleCalculationDetails: handleToggleCalculationDetails,
              professionId: currentProfessionId,
            };

            if (hasSearchOrFilter) {
              return (
                <PrimesList
                  {...commonListProps}
                  primesWithCalculatedAmounts={filteredPrimes}
                />
              );
            }

            return (
              <div>
                {activePrimesList.length > 0 && (
                  <PrimesCategoryAccordion
                    title="Primes actives"
                    primes={activePrimesList}
                    workTimePercentage={workTimePercentage}
                    defaultOpen={true}
                    isActiveGroup
                  >
                    <PrimesList
                      {...commonListProps}
                      primesWithCalculatedAmounts={activePrimesList}
                    />
                  </PrimesCategoryAccordion>
                )}

                {recommendedPrimes.filter(p => !p.active).length > 0 && (
                  <PrimesCategoryAccordion
                    title="Recommandées pour ton profil"
                    primes={recommendedPrimes.filter(p => !p.active)}
                    workTimePercentage={workTimePercentage}
                    defaultOpen={activePrimesList.length === 0}
                    isRecommended
                  >
                    <PrimesList
                      {...commonListProps}
                      primesWithCalculatedAmounts={recommendedPrimes.filter(p => !p.active)}
                    />
                  </PrimesCategoryAccordion>
                )}

                {primesByCategory.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Explorer toutes les primes</h4>
                    {primesByCategory.map(([category, primes]) => (
                      <PrimesCategoryAccordion
                        key={category}
                        title={category}
                        primes={primes}
                        workTimePercentage={workTimePercentage}
                        defaultOpen={false}
                      >
                        <PrimesList
                          {...commonListProps}
                          primesWithCalculatedAmounts={primes}
                        />
                      </PrimesCategoryAccordion>
                    ))}
                  </div>
                )}

                {filteredPrimes.length === 0 && (
                  <div className="text-center py-8 text-sm text-gray-500">
                    Aucune prime disponible pour ce profil.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Informations importantes */}
          <PrimesInfoSection />
        </>
    </div>
  );
}